"use client";

import { useEffect, useRef, useCallback, useState, memo } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "ui";
import { isSameDay } from "date-fns";
import type { OptimisticMessage, ChannelDetail } from "@/lib/types/channel";
import type { MessagesAction } from "../../store/messages-reducer";
import MessageItem, { type MessageSurface } from "./message-item";
import DateDivider from "./date-divider";
import ChannelWelcome from "./channel-welcome";

type Props = {
  channel?: ChannelDetail;
  messages: OptimisticMessage[];
  currentMemberId: string;
  isAdmin: boolean;
  hasOlderMessages: boolean;
  isLoadingOlder: boolean;
  dispatch: React.Dispatch<MessagesAction>;
  onLoadOlder: () => void;
  onReplyClick: (message: OptimisticMessage) => void;
  onRetry: (message: OptimisticMessage) => void;
  reactionsDisabled?: boolean;
  replyDisabled?: boolean;
  surface?: MessageSurface;
};

function MessageList({
  channel,
  messages,
  currentMemberId,
  isAdmin,
  hasOlderMessages,
  isLoadingOlder,
  dispatch,
  onLoadOlder,
  onReplyClick,
  onRetry,
  reactionsDisabled,
  replyDisabled,
  surface,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageCountRef = useRef(0);
  const isAtBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Auto-scroll to bottom on new messages (only if already at bottom)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const newCount = messages.length;
    const added = newCount - lastMessageCountRef.current;
    lastMessageCountRef.current = newCount;
    if (added > 0 && isAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  // Track whether user is at the bottom — drives both auto-scroll and the button
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    setShowScrollButton(!atBottom);
  }, []);

  // Scroll to bottom on initial load
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden">
      <div
        ref={scrollRef}
        className="flex flex-1 flex-col overflow-y-auto"
        onScroll={handleScroll}
      >
        {/* Spacer — pushes content to the bottom when messages don't fill the view */}
        <div className="flex-1" />

        {/* Load older messages button */}
        {hasOlderMessages && (
          <div className="flex justify-center py-3">
            <Button
              variant="outline"
              size="sm"
              onClick={onLoadOlder}
              disabled={isLoadingOlder}
              loading={isLoadingOlder}
              icon={<ChevronDown className="rotate-180" />}
            >
              Load older messages
            </Button>
          </div>
        )}

        {/* Channel welcome hero — shown at the very top when no older messages */}
        {!hasOlderMessages && channel && <ChannelWelcome channel={channel} />}

        <div className="flex flex-col py-2">
          {messages.map((message, index) => {
            const currentDate = new Date(message.createdAt);
            const prev = index > 0 ? messages[index - 1] : null;
            const prevDate = prev ? new Date(prev.createdAt) : null;
            const showDivider = !prevDate || !isSameDay(currentDate, prevDate);
            const isContinuation =
              !showDivider &&
              !!prev &&
              prev.member.id === message.member.id &&
              currentDate.getTime() - prevDate!.getTime() < 5 * 60 * 1000;

            return (
              <div key={message.id}>
                {showDivider && (
                  <DateDivider date={currentDate} isFirst={index === 0} />
                )}
                <MessageItem
                  message={message}
                  currentMemberId={currentMemberId}
                  isAdmin={isAdmin}
                  dispatch={dispatch}
                  onReplyClick={onReplyClick}
                  onRetry={onRetry}
                  isContinuation={isContinuation}
                  reactionsDisabled={reactionsDisabled}
                  replyDisabled={replyDisabled}
                  surface={surface}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Scroll-to-bottom button — only shown when user has scrolled up */}
      {showScrollButton && (
        <Button
          variant="outline"
          size="icon"
          onClick={scrollToBottom}
          icon={<ChevronDown />}
          title="Scroll to bottom"
          aria-label="Scroll to bottom"
          className="absolute bottom-3 right-3 rounded-full shadow-md"
        />
      )}
    </div>
  );
}

export default memo(MessageList);
